import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface SingleSelectOption {
  label: string;
  value: string;
}

interface SingleSelectPickerProps {
  label: string;
  options: SingleSelectOption[];
  selectedValue?: string;
  onSelectionChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}

export const SingleSelectPicker: React.FC<SingleSelectPickerProps> = ({
  label,
  options,
  selectedValue,
  onSelectionChange,
  placeholder = 'Select an option',
  error,
  required = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleOptionSelect = (value: string) => {
    onSelectionChange(value);
    setIsModalVisible(false);
  };

  const getDisplayText = () => {
    if (!selectedValue) {
      return placeholder;
    }
    const selectedOption = options.find(option => option.value === selectedValue);
    return selectedOption ? selectedOption.label : placeholder;
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {label}
        {required && <Text style={styles.required}> *</Text>}
      </Text>
      
      <TouchableOpacity
        style={[styles.selector, error && styles.selectorError]}
        onPress={() => setIsModalVisible(true)}
        activeOpacity={0.7}
      >
        <Text style={[
          styles.selectorText,
          !selectedValue && styles.placeholderText
        ]}>
          {getDisplayText()}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN
          width={20}
          height={20}
          color={ADMIN_COLORS.TEXT_SECONDARY}
        />
      </TouchableOpacity>

      {error && <Text style={styles.errorText}>{error}</Text>}

      {/* Selection Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => setIsModalVisible(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <Text style={styles.modalTitle}>{label}</Text>
            
            <TouchableOpacity
              style={styles.doneButton}
              onPress={() => setIsModalVisible(false)}
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>

          {/* Options List */}
          <ScrollView style={styles.optionsList}>
            {options.map((option) => {
              const isSelected = selectedValue === option.value;
              
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.option, isSelected && styles.optionSelected]}
                  onPress={() => handleOptionSelect(option.value)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.optionText, isSelected && styles.optionTextSelected]}>
                    {option.label}
                  </Text>
                  <View style={[styles.radioButton, isSelected && styles.radioButtonSelected]}>
                    {isSelected && <View style={styles.radioButtonInner} />}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginBottom: 8,
  },
  required: {
    color: ADMIN_COLORS.ERROR,
  },
  selector: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 48,
  },
  selectorError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  selectorText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  placeholderText: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  errorText: {
    fontSize: 12,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  modal: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    backgroundColor: ADMIN_COLORS.SURFACE,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  doneButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  doneButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.PRIMARY,
  },
  optionsList: {
    flex: 1,
  },
  option: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  optionSelected: {
    backgroundColor: ADMIN_COLORS.PRIMARY + '10',
  },
  optionText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  optionTextSelected: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '500',
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  radioButtonSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  radioButtonInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: ADMIN_COLORS.PRIMARY,
  },
});
